import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import Form, { ErrorMessage, Field } from '@atlaskit/form';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { CheckboxSelect, type ValueType } from '@atlaskit/select';
import { getUsers } from '@atlassian/eoc-auto-complete';
import { assert, AssertionError, isRequired, useValidators } from '@atlassian/eoc-common';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import {
	EXTRA_SELECT_PROPS,
	commonMessages as scheduleCommonMessages,
	TimezoneSelect,
} from '@atlassian/eoc-schedule-common';

import { DEFAULT_TIMES } from '../../../common/constants';
import { messages as commonMessages } from '../../../common/messages';
import { type SelectOption } from '../../../common/types';
import { AutoCompleteWithNoOne } from '../../../common/ui/auto-complete-with-noone';
import { type AutoCompleteWithNoOneItem } from '../../../common/ui/auto-complete-with-noone/custom-option/types';
import { OverrideVariant } from '../../../controllers/page-store/state/types';
import { useStore as useScheduleStore } from '../../../controllers/schedule-store';
import {
	useTimelineEpoch,
	useTimelineRotations,
} from '../../../controllers/schedule-store/helpers';
import { getScheduleRightsResource } from '../../../services/resources';
import { partitionDate } from '../../../utils/date';
import { isEmpty } from '../../../utils/validators';

import { confirmTextButtons } from './constants';
import { messages } from './messages';
import { FirstField } from './styled';
import { type AddOverrideProps, type CheckboxSelectOption, type FormFields } from './types';
import {
	convertRotationsToOptions,
	formatDateForProp,
	useDefaultUser,
	useDefaultValues,
} from './utils';

const AddOverride = ({ baseOverride, onClose, variant }: AddOverrideProps) => {
	const intlApi = useIntl();
	const { data: scheduleRights } = useResource(getScheduleRightsResource);
	const [
		{ schedule, getTimelineLoading, addOverrideLoading },
		{ addOverrideAction, updateOverrideAction },
	] = useScheduleStore();
	const [{ nowEpoch }] = useTimelineEpoch();
	const [rotations] = useTimelineRotations();

	const rotationOptions = useMemo(() => convertRotationsToOptions(rotations), [rotations]);

	if (variant === OverrideVariant.Edit || variant === OverrideVariant.Customize) {
		assert(
			!isEmpty(baseOverride),
			new AssertionError(
				'eoc-schedule',
				'add-override: baseOverride is empty in edit or customize variant',
			),
		);
	}

	if (variant === OverrideVariant.Edit) {
		assert(
			!isEmpty(baseOverride?.alias) && !isEmpty(baseOverride?.responder),
			new AssertionError(
				'eoc-schedule',
				'add-override: alias or responder (within baseOverride) is empty in edit variant',
			),
		);
	}

	const isRequiredValidator = useValidators([isRequired()]);

	const titleVariants = useMemo(
		() => ({
			[OverrideVariant.Edit]: intlApi.formatMessage(messages.editTitle),
			[OverrideVariant.Add]: intlApi.formatMessage(messages.addTitle),
			[OverrideVariant.Customize]: intlApi.formatMessage(messages.customizeTitle),
		}),
		[intlApi],
	);

	const confirmText = useMemo(() => confirmTextButtons(intlApi), [intlApi]);

	const isStartDisabled = useMemo(() => {
		// NOTE: consider adding buffer in order to prevent edge cases
		if (variant === OverrideVariant.Edit && baseOverride!.startEpoch! < nowEpoch) {
			return true;
		}

		return false;
	}, [baseOverride, nowEpoch, variant]);

	const defaultValues = useDefaultValues({
		base: baseOverride,
		variant,
		schedule,
	});

	const {
		isError: isUserError,
		isLoading: isUserLoading,
		user,
	} = useDefaultUser({
		variant,
		base: baseOverride,
	});

	return (
		<Form<FormFields>
			onSubmit={({ rotation, end, start, responder, timezone }) => {
				if (variant === OverrideVariant.Edit) {
					updateOverrideAction({
						startTime: partitionDate(new Date(start)),
						endTime: partitionDate(new Date(end)),
						rotationIds: rotation?.map(({ value }) => value),
						timezone: timezone.value,
						alias: baseOverride!.alias!,
						responder: {
							id: responder.atlassianIdentifier!,
							type: responder.type,
						},
					});
				} else {
					addOverrideAction({
						startTime: partitionDate(new Date(start)),
						endTime: partitionDate(new Date(end)),
						rotationIds: rotation?.map(({ value }) => value),
						timezone: timezone.value,
						responder: {
							id: responder.atlassianIdentifier!,
							type: responder.type,
						},
					});
				}
			}}
		>
			{({ formProps, getState }) => (
				<form {...formProps}>
					<ModalHeader>
						<ModalTitle>{titleVariants[variant]}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<FirstField>
							<Field<ValueType<SelectOption>>
								name="timezone"
								label={intlApi.formatMessage(messages.timezone)}
								defaultValue={defaultValues.timezone}
							>
								{({ fieldProps }) => (
									// @ts-ignore - https://product-fabric.atlassian.net/browse/DSP-21000
									<TimezoneSelect {...fieldProps} {...EXTRA_SELECT_PROPS} autoFocus />
								)}
							</Field>
						</FirstField>
						<Field
							name="start"
							label={intlApi.formatMessage(scheduleCommonMessages.from)}
							isRequired
							isDisabled={isStartDisabled}
							defaultValue={defaultValues.start}
							validate={(value) => {
								if (isStartDisabled) {
									return;
								}

								if (isEmpty(value)) {
									return intlApi.formatMessage(commonMessages.required);
								}

								// TODO: Use final form and format value using current timezone
								// const d = new Date(value);
								// if (isBefore(addMinutes(d, 5), nowEpoch)) { // add 5 minutes for buffer
								//   return intlApi.formatMessage(commonMessages.timePast);
								// }

								if (isEmpty(getState().values.end)) {
									return;
								}

								if (new Date(value).valueOf() > new Date(getState().values.end).valueOf()) {
									return intlApi.formatMessage(commonMessages.startLaterEnd);
								}
							}}
						>
							{({ fieldProps, error }) => (
								<>
									<DateTimePicker
										{...fieldProps}
										datePickerProps={{
											minDate: formatDateForProp(nowEpoch),
											weekStartDay: 1,
											label: intlApi
												.formatMessage(messages.override, {
													label: intlApi.formatMessage(commonMessages.startDate),
												})
												.toLowerCase(),
										}}
										timePickerProps={{
											times: DEFAULT_TIMES,

											label: intlApi
												.formatMessage(messages.override, {
													label: intlApi.formatMessage(commonMessages.startTime),
												})
												.toLowerCase(),

											timeIsEditable: true,
										}}
									/>
									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>
						<Field
							name="end"
							label={intlApi.formatMessage(scheduleCommonMessages.to)}
							isRequired
							defaultValue={defaultValues.end}
							validate={(value) => {
								if (isEmpty(value)) {
									return intlApi.formatMessage(commonMessages.required);
								}

								// TODO: Use final form and format value using current timezone
								// if (isBefore(new Date(value), nowEpoch)) { // add 5 minute buffer
								//   return intlApi.formatMessage(commonMessages.timePast);
								// }

								if (isEmpty(getState().values.start)) {
									return;
								}

								if (new Date(value).valueOf() < new Date(getState().values.start).valueOf()) {
									return intlApi.formatMessage(commonMessages.endEarlierStart);
								}
							}}
						>
							{({ fieldProps, error }) => {
								return (
									<>
										<DateTimePicker
											{...fieldProps}
											datePickerProps={{
												minDate: formatDateForProp(nowEpoch),
												weekStartDay: 1,
												label: intlApi
													.formatMessage(messages.override, {
														label: intlApi.formatMessage(commonMessages.endDate),
													})
													.toLowerCase(),
											}}
											timePickerProps={{
												times: DEFAULT_TIMES,

												label: intlApi
													.formatMessage(messages.override, {
														label: intlApi.formatMessage(commonMessages.endTime),
													})
													.toLowerCase(),

												timeIsEditable: true,
											}}
										/>
										{error && <ErrorMessage>{error}</ErrorMessage>}
									</>
								);
							}}
						</Field>
						<Field<ValueType<CheckboxSelectOption, true>>
							name="rotation"
							label={intlApi.formatMessage(messages.selectRotation)}
							defaultValue={defaultValues.rotation}
						>
							{({ fieldProps }) => (
								<CheckboxSelect
									{...fieldProps}
									{...EXTRA_SELECT_PROPS}
									{...(getTimelineLoading && { isLoading: true })}
									placeholder={intlApi.formatMessage(messages.allRotations)}
									options={rotationOptions}
								/>
							)}
						</Field>
						<Field<ValueType<AutoCompleteWithNoOneItem, false>>
							name="responder"
							label={intlApi.formatMessage(messages.overrideShiftParticipantWith)}
							defaultValue={user}
							validate={isRequiredValidator}
						>
							{({ fieldProps }) => (
								<AutoCompleteWithNoOne
									{...fieldProps}
									{...EXTRA_SELECT_PROPS}
									{...(isUserLoading && { isLoading: true })}
									{...(isUserError && { isError: true })}
									endpoints={[getUsers]}
									isDisabled={!scheduleRights!.hasEditRight}
								/>
							)}
						</Field>
					</ModalBody>
					<ModalFooter>
						<ButtonGroup>
							<Button appearance="subtle" onClick={onClose}>
								{intlApi.formatMessage(commonMessages.cancel)}
							</Button>
							<Button appearance="primary" isLoading={addOverrideLoading} type="submit">
								{confirmText[variant]}
							</Button>
						</ButtonGroup>
					</ModalFooter>
				</form>
			)}
		</Form>
	);
};

export { AddOverride };
